import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@bikes/routes/Routes';
import { useBikesStore } from '@bikes/store/store';
import {
  BikesType,
  BikesTypeLabel,
  emptyServiceAssetDetails,
  emptyServiceLocationDetails,
} from '@bikes/store/storeSlices/bikesDetails';
import { MapTemplate } from '@templates';
import { ErrorMessages } from '@types';
import {
  addressResponseType,
  addressSchema,
  convertAddressToServiceLocation,
  convertServiceLocationToAddress,
} from '@utils';
import { SelectedLocationTextContent } from './SelectedLocationTextContent';
import { LatLngTuple } from 'leaflet';
import { ZodError } from 'zod';

export const BikesLocation: FunctionComponent = () => {
  const navigate = useNavigate();
  const {
    bikesType,
    updateServiceLocationDetails,
    serviceLocationDetails,
    updateServiceAssetDetails,
    isPhotoLocationStored,
  } = useBikesStore();

  const { longitude, latitude } = serviceLocationDetails;

  const defaultLocation =
    latitude && longitude ? ([latitude, longitude] as LatLngTuple) : undefined;
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const errorPath = `${bikesType}-Location`;
  const pageTitle = isPhotoLocationStored
    ? 'Check if the location is correct'
    : `Where is the ${BikesTypeLabel[bikesType as BikesType]} located?`;

  useEffect(
    () => {
      if (!bikesType) {
        navigate(RoutesList.TEST_LANDING_PAGE);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const subText = isPhotoLocationStored ? (
    <div className="text-bodyLg flex flex-col gap-4">
      <p>We used your photo to find where the issue is located.</p>
      <p>You can change where the issue is located using the map.</p>
    </div>
  ) : (
    <p className="text-bodyXl">Select a location on the map.</p>
  );

  const handleOnNextClick = async () => {
    setErrorMessages([]);
    try {
      addressSchema.parse(serviceLocationDetails);
    } catch (error) {
      if (error instanceof ZodError) {
        const parsedErrorMessage = error.issues[0].message;
        setErrorMessages([{ path: errorPath, message: parsedErrorMessage }]);
        throw new Error();
      }
    }

    if (bikesType === BikesType.Bikes) {
      navigate(RoutesList.TELL_US_MORE);
    } else {
      navigate(RoutesList.WHAT_HIRE_COMPANY);
    }
  };

  const handleOnPreviousClick = () => {
    navigate(RoutesList.UPLOAD_PHOTO);
  };

  const buttonPrevious = {
    label: 'Previous',
    onClick: handleOnPreviousClick,
  };

  const buttonNext = {
    label: 'Next',
    onClick: handleOnNextClick,
  };

  const handleAddressChange = async (address: addressResponseType) => {
    setErrorMessages([]);
    if (Object.keys(address).length === 0) {
      // Clears address and asset details when user clears address from search bar
      updateServiceLocationDetails(emptyServiceLocationDetails);
      updateServiceAssetDetails(emptyServiceAssetDetails);
      return;
    } else {
      updateServiceLocationDetails(convertAddressToServiceLocation(address));
      setErrorMessages([]);
    }
  };

  return (
    <MapTemplate
      id={errorPath}
      labelText={pageTitle}
      subText={subText}
      errorPath={errorPath}
      errorMessages={errorMessages}
      onChangeAddress={handleAddressChange}
      defaultLocation={defaultLocation}
      selectedAddress={convertServiceLocationToAddress(serviceLocationDetails)}
      customAddressContent={<SelectedLocationTextContent />}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
    />
  );
};
